import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { API_URL } from "../constants"

const initialState = {
  adminStaff: [],
  adminStaffDetails: null,
  adminStaffInfo: null, // Added adminStaffInfo to track logged in admin staff
  loading: false,
  error: null,
  success: false,
}

// Get all admin staff
export const getAdminStaff = createAsyncThunk("adminStaff/getAll", async (_, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.get(`${API_URL}/api/admin-staff`, config)
    return data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

// Get admin staff by ID
export const getAdminStaffById = createAsyncThunk("adminStaff/getById", async (id, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.get(`${API_URL}/api/admin-staff/${id}`, config)
    return data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

// Create admin staff
export const createAdminStaff = createAsyncThunk(
  "adminStaff/create",
  async (adminStaffData, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(`${API_URL}/api/admin-staff`, adminStaffData, config)
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Update admin staff
export const updateAdminStaff = createAsyncThunk(
  "adminStaff/update",
  async ({ id, adminStaffData }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.put(`${API_URL}/api/admin-staff/${id}`, adminStaffData, config)
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Delete admin staff
export const deleteAdminStaff = createAsyncThunk("adminStaff/delete", async (id, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    await axios.delete(`${API_URL}/api/admin-staff/${id}`, config)
    return id
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

// Logout admin staff
export const logoutAdminStaff = createAsyncThunk("adminStaff/logout", async () => {
  localStorage.removeItem("adminStaffInfo")
  return null
})

const adminStaffSlice = createSlice({
  name: "adminStaff",
  initialState,
  reducers: {
    resetAdminStaffState: (state) => {
      state.loading = false
      state.error = null
      state.success = false
    },
    clearAdminStaffDetails: (state) => {
      state.adminStaffDetails = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all admin staff
      .addCase(getAdminStaff.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAdminStaff.fulfilled, (state, action) => {
        state.loading = false
        state.adminStaff = action.payload
      })
      .addCase(getAdminStaff.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Get admin staff by ID
      .addCase(getAdminStaffById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAdminStaffById.fulfilled, (state, action) => {
        state.loading = false
        state.adminStaffDetails = action.payload
      })
      .addCase(getAdminStaffById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create admin staff
      .addCase(createAdminStaff.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createAdminStaff.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.adminStaff.unshift(action.payload)
      })
      .addCase(createAdminStaff.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update admin staff
      .addCase(updateAdminStaff.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateAdminStaff.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.adminStaff = state.adminStaff.map((staff) => (staff._id === action.payload._id ? action.payload : staff))
        state.adminStaffDetails = action.payload
      })
      .addCase(updateAdminStaff.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Delete admin staff
      .addCase(deleteAdminStaff.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteAdminStaff.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.adminStaff = state.adminStaff.filter((staff) => staff._id !== action.payload)
      })
      .addCase(deleteAdminStaff.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Logout admin staff
      .addCase(logoutAdminStaff.fulfilled, (state) => {
        state.adminStaffInfo = null
      })
  },
})

export const { resetAdminStaffState, clearAdminStaffDetails } = adminStaffSlice.actions

export default adminStaffSlice.reducer
